import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  ApiQueryParams,
  CatalogProduct,
  CatalogProductGetResponse,
  CatalogProductListResponse,
  CatalogProductsService,
} from 'src/api/generated';
import { ApiResponse, useMemoizedArrayData } from 'src/hooks/api/api';

/**
 * Parameters for the useProducts hook.
 * Extends ApiQueryParams with an optional enabled flag to control query execution.
 */
interface UseProductsParams extends ApiQueryParams {
  /** Whether the query is enabled. Defaults to true. Disable to prevent query execution. */
  enabled?: boolean;
}

/**
 * Response object returned by the useProducts hook.
 * Contains paginated product data and pagination controls.
 */
interface ProductsResponse extends ApiResponse {
  /** Array of product items from the current pages. */
  products: CatalogProduct[];
  /** Function to fetch the next page of results. */
  fetchNextPage: () => Promise<unknown>;
  /** Whether there are more pages available to fetch. */
  hasNextPage: boolean;
  /** Whether a next page is currently being fetched. */
  isFetchingNextPage: boolean;
  /** Token for fetching the next page of results. */
  nextPageToken?: string;
  /** Total count of products matching the query filters. */
  totalCount?: number;
}

/**
 * Hook for fetching a paginated list of catalog products.
 * Supports filtering, searching, sorting, and infinite pagination.
 *
 * @param params - Query parameters including filters, search, sort, pagination, and enabled flag
 * @returns Response object containing products array, pagination state, and control functions
 *
 * @example
 * const { products, fetchNextPage, hasNextPage, loading } = useProducts({
 *   filterBy: { category: 'electronics' },
 *   sortBy: { field: 'name', direction: 'asc' },
 *   enabled: true,
 * });
 */
export const useProducts = (
  params: UseProductsParams = {}
): ProductsResponse => {
  const { enabled = true, ...queryParams } = params;

  const fetchProducts = async ({
    pageParam,
  }: {
    pageParam: string | undefined;
  }): Promise<CatalogProductListResponse> => {
    const { filterBy, searchBy, sortBy, page, pageSize } = queryParams;

    return await CatalogProductsService.listProducts(
      filterBy,
      searchBy,
      sortBy,
      page,
      pageSize,
      pageParam
    );
  };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['catalog/products', queryParams],
    queryFn: fetchProducts,
    getNextPageParam: (lastPage) => lastPage.nextPageToken,
    initialPageParam: undefined,
    enabled,
  });

  const products = useMemoizedArrayData<CatalogProduct>(
    data?.pages.flatMap((page) => page.items) ?? []
  );

  return {
    loading,
    error,
    products,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  };
};

/**
 * Response object returned by the useProduct hook.
 * Contains a single product detail or null if not loaded.
 */
interface UseProductResponse extends ApiResponse {
  /** The product detail object, or null if not yet loaded or not found. */
  product: CatalogProduct | null;
}

/**
 * Hook for fetching a single product by ID.
 * Returns the product detail with loading and error states.
 *
 * @param id - The unique identifier of the product to fetch
 * @param enabled - Whether the query is enabled. Set to false to prevent fetching.
 * @returns Response object containing product data, loading state, errors, and refetch function
 *
 * @example
 * const { product, loading, error, refetch } = useProduct(123, true);
 * if (loading) return <div>Loading...</div>;
 * if (error) return <div>Failed to load product</div>;
 * return <div>{product?.name}</div>;
 */
export const useProduct = (
  id: number,
  enabled: boolean
): UseProductResponse => {
  const fetchProduct = async (): Promise<CatalogProductGetResponse> => {
    return await CatalogProductsService.getProduct(id);
  };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['catalog/products', 'detail', id],
    queryFn: fetchProduct,
    enabled,
  });

  return {
    loading,
    error,
    product: data ?? null,
    refetch,
  };
};
